import { useState } from "react";
import styles from "./EditArticlesOrderedDetails.module.css";
import ErrorAddOn from "../TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";
import { useTranslation } from "@coworker/locales";
import { ItemNoAndTypeWithOrderedDetails } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { OrderedDetailsMode } from "@coworker/apprestructured/src/task-management/types/OrderedDetailsMode";
import { isLoading, Loading } from "../../loading";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { EditArticlesOrderedDetailsContent } from "./EditArticlesOrderedDetailsContent/EditArticlesOrderedDetailsContent";
import { useAllAttachedItems } from "@coworker/apprestructured/src/task-management/services/useAllAttachedItems";
import { ConfirmSheetModal } from "@coworker/apprestructured/src/shared/components/ConfirmSheetModal/ConfirmSheetModal";
import { useItemsWithPieces } from "../../services/useItemsWithPieces";

interface EditArticlesOrderedDetailsProps {
  disabled?: boolean;
  isError?: boolean;
  mode: OrderedDetailsMode;
  itemNosAndTypeWithOrderedDetails:
    ItemNoAndTypeWithOrderedDetails[] | null | Loading;
  onCompleteChange: (
    itemNo: string,
    field: "pieces_ordered" | "ptags_ordered",
    completed: boolean
  ) => void;
}

export const EditArticlesOrderedDetails = ({
  mode,
  disabled,
  isError,
  itemNosAndTypeWithOrderedDetails,
  onCompleteChange,
}: EditArticlesOrderedDetailsProps) => {
  const [showModal, setShowModal] = useState(false);
  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);
  const { t } = useTranslation();
  const { items, isFetchingItems } = useAllAttachedItems(
    itemNosAndTypeWithOrderedDetails ?? []
  );
  const itemsWithPieces = useItemsWithPieces(
    items,
    itemNosAndTypeWithOrderedDetails ?? []
  );

  if (isLoading(itemNosAndTypeWithOrderedDetails) || isFetchingItems) {
    return <div>Loading...</div>;
  }

  const handleConfirmWithClose = () => {
    handleCloseModal();
  };

  const itemsAggregates = Array.isArray(itemNosAndTypeWithOrderedDetails)
    ? itemNosAndTypeWithOrderedDetails
    : [];
  const total = mode.getTotalQuantity(itemsAggregates);

  return (
    <div>
      {/* Title section: always rendered at full opacity, mirroring
          TitledBoxedTextWithModal (used by MediasQuantityViewerFragment),
          which is already known to look correct on a closed/disabled task. */}
      <div className={styles["titleSection"]}>
        <FixaText textStyle="Heading.S">{t(mode.titleTranslationKey)}</FixaText>
        <FixaText textStyle="Body.L">{t(mode.subTitleTranslationKey)}</FixaText>
      </div>

      {/* List item: the count is the list item's own title, so Skapa's
          built-in disabled dimming applies to it directly -- no custom
          disabled styling needed. */}
      <FixaListView
        id={`${mode.titleTranslationKey}-list`}
        showDivider={false}
        size="small"
      >
        <FixaListViewItem
          inset
          className={styles["listViewItemBackground"]}
          data-testid={`${mode.titleTranslationKey}-list-item`}
          title={
            isError ? undefined : t(mode.totalTranslationKey, { count: total })
          }
          control="navigational"
          controlIcon={isError ? null : "chevronRight"}
          disabled={disabled}
          type="button"
          onClick={handleOpenModal}
          emphasised={true}
          showDivider={false}
          addOn={
            isError ? (
              <ErrorAddOn testId="ordered-details-error">
                {t("pleaseFillInString")}
              </ErrorAddOn>
            ) : undefined
          }
        />
      </FixaListView>

      <ConfirmSheetModal
        title={t(mode.titleTranslationKey)}
        showModal={showModal}
        handleCloseModal={handleCloseModal}
        handleConfirm={handleConfirmWithClose}
        content={
          <div>
            <EditArticlesOrderedDetailsContent
              items={itemsWithPieces}
              itemNosAndTypeWithOrderedDetails={
                itemNosAndTypeWithOrderedDetails
              }
              fieldConfigs={mode.fields}
              onCompleteChange={onCompleteChange}
            />
          </div>
        }
      />
    </div>
  );
};
